// components/IdentifierInput.js
//
// The single "email or mobile number" field of the signup page and the RSVP
// popup. Typing an email shows the mail icon, as before. Starting with a digit
// swaps it for a country code picker - US (+1) first, then India (+91), then
// the rest - because a bare "98765 43210" or "(415) 555-0100" does not say
// which country it is in. It starts on the visitor's own country (the parent
// holds it: hooks/usePhoneCountry), or blank - "Code" - when that cannot be
// told; `nudge` (hooks/useCountryNudge) shakes and rings it when the form is
// sent without one. Typing "+" and a code needs no picker.
//
// The picker is a native <select> laid invisibly over a small flag-and-code
// label: the phone's own country list on mobile, the keyboard on desktop,
// and nothing to style but the label.
//
// Parsing is shared/authOptions parseIdentifier(value, country.code).

import React from 'react';
import { FiMail, FiChevronDown } from 'react-icons/fi';
import { PHONE_COUNTRIES, looksLikeLocalPhone } from '../shared/authOptions';
import { CountryCodeHint } from '../hooks/useCountryNudge';
import '../styles/IdentifierInput.scss';

/** Left padding that clears the picker: its 8px inset, its width, 10px air. */
const pickerRoom = (label) => 8 + (56 + 9 * label.length) + 10;

export default function IdentifierInput({
  id,
  value,
  onChange,
  country,
  onCountryChange,
  nudge,
  ...inputProps
}) {
  const showCountry = looksLikeLocalPhone(value);
  const selected = country || null;
  const codeLabel = selected ? `+${selected.code}` : 'Code';

  return (
    <>
    <div className={`input-with-icon identifier-input${showCountry ? ' has-country' : ''}`}>
      {showCountry ? (
        <label
          ref={nudge?.ref}
          className={`identifier-country ${nudge?.className ?? ''}`}
          title={selected ? `${selected.name} (+${selected.code})` : 'Pick your country code'}
        >
          <span className="identifier-country-flag" aria-hidden="true">{selected?.flag ?? '🌐'}</span>
          <span className="identifier-country-code">{codeLabel}</span>
          <FiChevronDown className="identifier-country-caret" aria-hidden="true" />
          <select
            aria-label="Country code"
            value={selected?.iso2 ?? ''}
            onChange={(e) => onCountryChange(PHONE_COUNTRIES.find((c) => c.iso2 === e.target.value))}
          >
            {!selected && <option value="" disabled>Pick your country code</option>}
            {PHONE_COUNTRIES.map((c) => (
              <option key={c.iso2} value={c.iso2}>{`${c.flag} ${c.name} +${c.code}`}</option>
            ))}
          </select>
        </label>
      ) : (
        <FiMail className="input-icon" />
      )}
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        {...inputProps}
        // Room for the picker instead of the mail icon, sized to its code
        // (+1 is narrower than +971). Inline, because each page styles
        // .input-with-icon input padding its own way.
        style={showCountry ? { ...inputProps.style, paddingLeft: pickerRoom(codeLabel) } : inputProps.style}
      />
    </div>
    {showCountry && <CountryCodeHint nudge={nudge} />}
    </>
  );
}
